import { useState, useEffect } from 'react';
import { useNavigate, Link } from '@/lib/router-compat';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/hooks/use-toast';
import { DollarSign, ArrowLeft, ArrowRight, Check } from 'lucide-react';
import { z } from 'zod';

const steps = ['Loan Details', 'Personal Info', 'Employment', 'Review'];

const loanSchema = z.object({
  loanType: z.string().min(1, 'Please select a loan type'),
  loanAmount: z.number().min(1000, 'Minimum amount is $1,000').max(100000, 'Maximum amount is $100,000'),
  loanTerm: z.number().min(12, 'Minimum term is 12 months'),
  purpose: z.string().optional(),
});

const personalSchema = z.object({
  fullName: z.string().min(2, 'Name is required'),
  phone: z.string().min(10, 'Valid phone number required'),
  address: z.string().min(5, 'Address is required'),
  city: z.string().min(2, 'City is required'),
  state: z.string().min(2, 'State is required'),
  zipCode: z.string().min(5, 'ZIP code is required'),
  dateOfBirth: z.string().min(1, 'Date of birth is required'),
  ssnLastFour: z.string().length(4, 'Last 4 digits of SSN required'),
});

const employmentSchema = z.object({
  employmentStatus: z.string().min(1, 'Employment status required'),
  employerName: z.string().optional(),
  annualIncome: z.number().min(0, 'Annual income required'),
});

export default function Apply() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [currentStep, setCurrentStep] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Form data
  const [formData, setFormData] = useState({
    loanType: '',
    loanAmount: 10000,
    loanTerm: 36,
    purpose: '',
    fullName: '',
    phone: '',
    address: '',
    city: '',
    state: '',
    zipCode: '',
    dateOfBirth: '',
    ssnLastFour: '',
    employmentStatus: '',
    employerName: '',
    annualIncome: 0,
  });

  useEffect(() => {
    if (!loading && !user) {
      navigate('/auth');
    }
  }, [user, loading, navigate]);

  useEffect(() => {
    if (user) {
      fetchProfile();
    }
  }, [user]);

  const fetchProfile = async () => {
    const { data } = await supabase
      .from('profiles')
      .select('*')
      .eq('user_id', user!.id)
      .maybeSingle();

    if (data) {
      setFormData(prev => ({
        ...prev,
        fullName: data.full_name || '',
        phone: data.phone || '',
        address: data.address || '',
        city: data.city || '',
        state: data.state || '',
        zipCode: data.zip_code || '',
        dateOfBirth: data.date_of_birth || '',
        ssnLastFour: data.ssn_last_four || '',
        employmentStatus: data.employment_status || '',
        employerName: data.employer_name || '',
        annualIncome: Number(data.annual_income) || 0,
      }));
    }
  };

  const updateFormData = (field: string, value: string | number) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setErrors(prev => ({ ...prev, [field]: '' }));
  };

  const validateStep = () => {
    let schema;
    let data;

    switch (currentStep) {
      case 0:
        schema = loanSchema;
        data = {
          loanType: formData.loanType,
          loanAmount: formData.loanAmount,
          loanTerm: formData.loanTerm,
          purpose: formData.purpose,
        };
        break;
      case 1:
        schema = personalSchema;
        data = {
          fullName: formData.fullName,
          phone: formData.phone,
          address: formData.address,
          city: formData.city,
          state: formData.state,
          zipCode: formData.zipCode,
          dateOfBirth: formData.dateOfBirth,
          ssnLastFour: formData.ssnLastFour,
        };
        break;
      case 2:
        schema = employmentSchema;
        data = {
          employmentStatus: formData.employmentStatus,
          employerName: formData.employerName,
          annualIncome: formData.annualIncome,
        };
        break;
      default:
        return true;
    }

    const result = schema.safeParse(data);
    if (!result.success) {
      const newErrors: Record<string, string> = {};
      result.error.errors.forEach(err => {
        const field = err.path[0] as string;
        newErrors[field] = err.message;
      });
      setErrors(newErrors);
      return false;
    }

    return true;
  };

  const nextStep = () => {
    if (validateStep()) {
      setCurrentStep(prev => Math.min(prev + 1, steps.length - 1));
    }
  };

  const prevStep = () => {
    setCurrentStep(prev => Math.max(prev - 1, 0));
  };

  const handleSubmit = async () => {
    if (!user) return;

    setIsSubmitting(true);

    try {
      // Update profile
      await supabase
        .from('profiles')
        .update({
          full_name: formData.fullName,
          phone: formData.phone,
          address: formData.address,
          city: formData.city,
          state: formData.state,
          zip_code: formData.zipCode,
          date_of_birth: formData.dateOfBirth,
          ssn_last_four: formData.ssnLastFour,
          employment_status: formData.employmentStatus,
          employer_name: formData.employerName,
          annual_income: formData.annualIncome,
        })
        .eq('user_id', user.id);

      // Create loan application
      const { error } = await supabase
        .from('loan_applications')
        .insert({
          user_id: user.id,
          loan_type: formData.loanType,
          loan_amount: formData.loanAmount,
          loan_term: formData.loanTerm,
          purpose: formData.purpose,
          status: 'pending',
        });

      if (error) throw error;

      toast({
        title: 'Application Submitted!',
        description: 'Your loan application has been submitted successfully. We\'ll review it shortly.',
      });

      navigate('/dashboard');
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to submit application. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
    }).format(amount);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-pulse text-muted-foreground">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="bg-card border-b border-border">
        <div className="container mx-auto px-6">
          <div className="flex items-center justify-between h-16">
            <Link to="/dashboard" className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors">
              <ArrowLeft className="h-4 w-4" />
              Back to Dashboard
            </Link>
            <Link to="/" className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg gradient-primary flex items-center justify-center">
                <DollarSign className="h-4 w-4 text-primary-foreground" />
              </div>
              <span className="font-display font-bold text-lg text-foreground">Quick Credit</span>
            </Link>
          </div>
        </div>
      </header>

      {/* Progress Steps */}
      <div className="bg-card border-b border-border py-6">
        <div className="container mx-auto px-6">
          <div className="flex items-center justify-center gap-4">
            {steps.map((step, index) => (
              <div key={step} className="flex items-center gap-2">
                <div
                  className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-colors ${
                    index < currentStep
                      ? 'bg-success text-success-foreground'
                      : index === currentStep
                      ? 'bg-accent text-accent-foreground'
                      : 'bg-secondary text-muted-foreground'
                  }`}
                >
                  {index < currentStep ? <Check className="h-4 w-4" /> : index + 1}
                </div>
                <span className={`hidden sm:inline text-sm ${index === currentStep ? 'font-semibold' : 'text-muted-foreground'}`}>
                  {step}
                </span>
                {index < steps.length - 1 && (
                  <div className={`w-12 h-0.5 ${index < currentStep ? 'bg-success' : 'bg-border'}`} />
                )}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Form Content */}
      <main className="container mx-auto px-6 py-8 max-w-2xl">
        <Card className="shadow-card animate-fade-in">
          <CardHeader>
            <CardTitle className="font-display text-2xl">{steps[currentStep]}</CardTitle>
            <CardDescription>
              {currentStep === 0 && 'Tell us about the loan you need'}
              {currentStep === 1 && 'Provide your personal information'}
              {currentStep === 2 && 'Tell us about your employment'}
              {currentStep === 3 && 'Review and submit your application'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {/* Step 1: Loan Details */}
            {currentStep === 0 && (
              <div className="space-y-6">
                <div className="space-y-2">
                  <Label htmlFor="loanType">Loan Type</Label>
                  <Select value={formData.loanType} onValueChange={(v) => updateFormData('loanType', v)}>
                    <SelectTrigger className={errors.loanType ? 'border-destructive' : ''}>
                      <SelectValue placeholder="Select loan type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Personal Loan">Personal Loan</SelectItem>
                      <SelectItem value="Home Improvement">Home Improvement</SelectItem>
                      <SelectItem value="Debt Consolidation">Debt Consolidation</SelectItem>
                      <SelectItem value="Auto Loan">Auto Loan</SelectItem>
                      <SelectItem value="Business Loan">Business Loan</SelectItem>
                    </SelectContent>
                  </Select>
                  {errors.loanType && <p className="text-sm text-destructive">{errors.loanType}</p>}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="loanAmount">Loan Amount</Label>
                  <div className="flex items-center gap-4">
                    <Input
                      id="loanAmount"
                      type="range"
                      min="1000"
                      max="100000"
                      step="1000"
                      value={formData.loanAmount}
                      onChange={(e) => updateFormData('loanAmount', Number(e.target.value))}
                      className="flex-1"
                    />
                    <span className="font-display font-bold text-lg w-24 text-right">
                      {formatCurrency(formData.loanAmount)}
                    </span>
                  </div>
                  {errors.loanAmount && <p className="text-sm text-destructive">{errors.loanAmount}</p>}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="loanTerm">Loan Term</Label>
                  <Select value={String(formData.loanTerm)} onValueChange={(v) => updateFormData('loanTerm', Number(v))}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="12">12 months</SelectItem>
                      <SelectItem value="24">24 months</SelectItem>
                      <SelectItem value="36">36 months</SelectItem>
                      <SelectItem value="48">48 months</SelectItem>
                      <SelectItem value="60">60 months</SelectItem>
                      <SelectItem value="72">72 months</SelectItem>
                      <SelectItem value="84">84 months</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="purpose">Purpose (Optional)</Label>
                  <Textarea
                    id="purpose"
                    placeholder="Tell us how you plan to use the funds"
                    value={formData.purpose}
                    onChange={(e) => updateFormData('purpose', e.target.value)}
                  />
                </div>
              </div>
            )}

            {/* Step 2: Personal Info */}
            {currentStep === 1 && (
              <div className="space-y-6">
                <div className="grid grid-cols-2 gap-4">
                  <div className="col-span-2 space-y-2">
                    <Label htmlFor="fullName">Full Legal Name</Label>
                    <Input
                      id="fullName"
                      value={formData.fullName}
                      onChange={(e) => updateFormData('fullName', e.target.value)}
                      className={errors.fullName ? 'border-destructive' : ''}
                    />
                    {errors.fullName && <p className="text-sm text-destructive">{errors.fullName}</p>}
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="phone">Phone Number</Label>
                    <Input
                      id="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => updateFormData('phone', e.target.value)}
                      className={errors.phone ? 'border-destructive' : ''}
                    />
                    {errors.phone && <p className="text-sm text-destructive">{errors.phone}</p>}
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="dateOfBirth">Date of Birth</Label>
                    <Input
                      id="dateOfBirth"
                      type="date"
                      value={formData.dateOfBirth}
                      onChange={(e) => updateFormData('dateOfBirth', e.target.value)}
                      className={errors.dateOfBirth ? 'border-destructive' : ''}
                    />
                    {errors.dateOfBirth && <p className="text-sm text-destructive">{errors.dateOfBirth}</p>}
                  </div>

                  <div className="col-span-2 space-y-2">
                    <Label htmlFor="address">Street Address</Label>
                    <Input
                      id="address"
                      value={formData.address}
                      onChange={(e) => updateFormData('address', e.target.value)}
                      className={errors.address ? 'border-destructive' : ''}
                    />
                    {errors.address && <p className="text-sm text-destructive">{errors.address}</p>}
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="city">City</Label>
                    <Input
                      id="city"
                      value={formData.city}
                      onChange={(e) => updateFormData('city', e.target.value)}
                      className={errors.city ? 'border-destructive' : ''}
                    />
                    {errors.city && <p className="text-sm text-destructive">{errors.city}</p>}
                  </div>

                  <div className="grid grid-cols-2 gap-2">
                    <div className="space-y-2">
                      <Label htmlFor="state">State</Label>
                      <Input
                        id="state"
                        value={formData.state}
                        onChange={(e) => updateFormData('state', e.target.value)}
                        className={errors.state ? 'border-destructive' : ''}
                      />
                      {errors.state && <p className="text-sm text-destructive">{errors.state}</p>}
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="zipCode">ZIP</Label>
                      <Input
                        id="zipCode"
                        value={formData.zipCode}
                        onChange={(e) => updateFormData('zipCode', e.target.value)}
                        className={errors.zipCode ? 'border-destructive' : ''}
                      />
                      {errors.zipCode && <p className="text-sm text-destructive">{errors.zipCode}</p>}
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="ssnLastFour">Last 4 of SSN</Label>
                    <Input
                      id="ssnLastFour"
                      maxLength={4}
                      value={formData.ssnLastFour}
                      onChange={(e) => updateFormData('ssnLastFour', e.target.value.replace(/\D/g, ''))}
                      className={errors.ssnLastFour ? 'border-destructive' : ''}
                    />
                    {errors.ssnLastFour && <p className="text-sm text-destructive">{errors.ssnLastFour}</p>}
                  </div>
                </div>
              </div>
            )}

            {/* Step 3: Employment */}
            {currentStep === 2 && (
              <div className="space-y-6">
                <div className="space-y-2">
                  <Label htmlFor="employmentStatus">Employment Status</Label>
                  <Select value={formData.employmentStatus} onValueChange={(v) => updateFormData('employmentStatus', v)}>
                    <SelectTrigger className={errors.employmentStatus ? 'border-destructive' : ''}>
                      <SelectValue placeholder="Select status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Employed">Employed</SelectItem>
                      <SelectItem value="Self-Employed">Self-Employed</SelectItem>
                      <SelectItem value="Retired">Retired</SelectItem>
                      <SelectItem value="Student">Student</SelectItem>
                      <SelectItem value="Unemployed">Unemployed</SelectItem>
                    </SelectContent>
                  </Select>
                  {errors.employmentStatus && <p className="text-sm text-destructive">{errors.employmentStatus}</p>}
                </div>

                {(formData.employmentStatus === 'Employed' || formData.employmentStatus === 'Self-Employed') && (
                  <div className="space-y-2">
                    <Label htmlFor="employerName">Employer Name</Label>
                    <Input
                      id="employerName"
                      value={formData.employerName}
                      onChange={(e) => updateFormData('employerName', e.target.value)}
                    />
                  </div>
                )}

                <div className="space-y-2">
                  <Label htmlFor="annualIncome">Annual Income</Label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">$</span>
                    <Input
                      id="annualIncome"
                      type="number"
                      className={`pl-7 ${errors.annualIncome ? 'border-destructive' : ''}`}
                      value={formData.annualIncome || ''}
                      onChange={(e) => updateFormData('annualIncome', Number(e.target.value))}
                    />
                  </div>
                  {errors.annualIncome && <p className="text-sm text-destructive">{errors.annualIncome}</p>}
                </div>
              </div>
            )}

            {/* Step 4: Review */}
            {currentStep === 3 && (
              <div className="space-y-6">
                <div className="rounded-lg bg-secondary p-6 space-y-4">
                  <h3 className="font-display font-semibold text-lg">Loan Details</h3>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <p className="text-muted-foreground">Loan Type</p>
                      <p className="font-medium">{formData.loanType}</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">Amount</p>
                      <p className="font-medium">{formatCurrency(formData.loanAmount)}</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">Term</p>
                      <p className="font-medium">{formData.loanTerm} months</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">Est. Monthly Payment</p>
                      <p className="font-medium">
                        {formatCurrency(formData.loanAmount / formData.loanTerm * 1.07)}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="rounded-lg bg-secondary p-6 space-y-4">
                  <h3 className="font-display font-semibold text-lg">Personal Information</h3>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <p className="text-muted-foreground">Name</p>
                      <p className="font-medium">{formData.fullName}</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">Phone</p>
                      <p className="font-medium">{formData.phone}</p>
                    </div>
                    <div className="col-span-2">
                      <p className="text-muted-foreground">Address</p>
                      <p className="font-medium">
                        {formData.address}, {formData.city}, {formData.state} {formData.zipCode}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="rounded-lg bg-secondary p-6 space-y-4">
                  <h3 className="font-display font-semibold text-lg">Employment</h3>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <p className="text-muted-foreground">Status</p>
                      <p className="font-medium">{formData.employmentStatus}</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">Annual Income</p>
                      <p className="font-medium">{formatCurrency(formData.annualIncome)}</p>
                    </div>
                    {formData.employerName && (
                      <div>
                        <p className="text-muted-foreground">Employer</p>
                        <p className="font-medium">{formData.employerName}</p>
                      </div>
                    )}
                  </div>
                </div>

                <p className="text-sm text-muted-foreground">
                  By submitting this application, you agree to our Terms of Service and authorize us to verify your information.
                </p>
              </div>
            )}

            {/* Navigation Buttons */}
            <div className="flex justify-between mt-8 pt-6 border-t border-border">
              <Button
                variant="outline"
                onClick={prevStep}
                disabled={currentStep === 0}
              >
                <ArrowLeft className="h-4 w-4 mr-2" /> Back
              </Button>

              {currentStep < steps.length - 1 ? (
                <Button onClick={nextStep} className="bg-accent hover:bg-accent/90">
                  Continue <ArrowRight className="h-4 w-4 ml-2" />
                </Button>
              ) : (
                <Button
                  onClick={handleSubmit}
                  className="bg-accent hover:bg-accent/90"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? 'Submitting...' : 'Submit Application'}
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      </main>
    </div>
  );
}